import { getAccountDetails } from 'actions/accountActions';
import { logOut } from 'actions/userActions';
import Button from 'components/common/Button';
import { upperFirst } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { closeAccount, updatePlan, UpdatePlanRequest } from 'services/stripeService';
import styled from 'styled-components';
import { colors, containerStyles } from 'styles/shared';
import toaster from '../../../../helpers/uiHelper';
import PlanItem from './components/PlanItem';

const PlansContainer = styled.div`
  width: 54.4rem;
  height: 81.8rem;
  margin-bottom: 5rem;
  margin-right: 5rem;
  padding: 3.9rem 4.9rem 4.9rem;
  ${containerStyles};

  > div {
    margin-bottom: 3.5rem;
  }
`;

const ButtonOverride = styled(Button)`
  border: 0.2rem solid ${colors.blue_main};
`;

const planStyles = [
  {
    icon: 'start-up',
    color: colors.blue_darker,
    background: colors.blue_light,
    checkColor: colors.blue_main
  },
  {
    icon: 'growth',
    color: colors.green_darker,
    background: colors.green_lighter,
    checkColor: colors.green_light
  },
  {
    icon: 'scale',
    color: colors.purple_dark,
    background: colors.purple_lighter,
    checkColor: colors.purple_light
  }
];

SelectedPlansOptionsSection.propTypes = {
  plans: PropTypes.object.isRequired
};

function selectedPlan(plans, id) {
  for (const plan of plans) {
    if (plan.id === id) {
      return plan;
    }
  }
  return null;
}

function SelectedPlansOptionsSection({plans}) {
  const [displayPlans, setDisplayPlans] = useState([]);
  const [activePlan, setActivePlan] = useState('');
  const [activePlanName, setActivePlanName] = useState('');
  const [currentPlan, setCurrentPlan] = useState('');
  const dispatch = useDispatch();
  let history = useHistory();

  const user = useSelector((state: any) => state.user.current);

  useEffect(() => {
    let display: any = [];

    for (let key of Object.keys(plans)) {
      const plan = plans[key];

      let itemStyles = planStyles[display.length];

      let item = {
        id: plan.id,
        name: plan.name,
        overage: plan.overage,
        price: plan.price,
        requestQuota: plan.request_quota,
        price_id: plan.price_id,
        ...itemStyles
      };

      display.push(item);
    }

    setDisplayPlans(display);
    selectActivePlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans]);

  async function selectActivePlan() {
    const userId = user?.id;

    if (!userId) return;

    let accountDetails: any = await dispatch(getAccountDetails(userId));

    if (!accountDetails) return;

    setActivePlan(accountDetails.plan.id);
    setActivePlanName(accountDetails.plan.name);
    setCurrentPlan(accountDetails.plan.id);
  }

  function selectPlan(id) {
    const plan = selectedPlan(displayPlans, id);
    if (plan === null) {
      return;
    }
    setActivePlan(id);
    for (let key of Object.keys(plans)) {
      const plan = plans[key];

      if (plan.id === id) {
        setActivePlanName(plan.name);
        break;
      }
    }
  }

  const handleCancellation = async event => {
    event.preventDefault();

    if (!user) {
      console.log('user not authenticated');
      return;
    }

    if (!user.customer_id) {
      console.log('user not signed up');
      return;
    }

    let result = await closeAccount();
    if (result !== true) {
      toaster.showError(
        "Could not cancel your account. Please make sure you've paid any outstanding bills. If you think this is a mistake, pleae contact us via the support form."
      );
    } else {
      toaster.showMessage(
        `Your account has been closed. We're really sorry to see you go! Feel free to sign up again anytime.`
      );

      dispatch(logOut());
      history.push('/login');
    }
  };

  const handlePlanChange = async event => {
    event.preventDefault();

    if (!user) {
      console.log('user not authenticated');
      return;
    }

    if (!user.customer_id) {
      console.log('user not signed up');
      return;
    }

    let result = await updatePlan({NewPlan: activePlanName} as UpdatePlanRequest);
    if (result !== true) {
      toaster.showError('Could not update subscription');
    } else {
      toaster.showMessage(`You're now on the ${activePlanName} plan`);
    }
  };

  function render() {
    return (
      <PlansContainer>
        {displayPlans.map((plan: any) => {
          return <PlanItem key={plan.id} plan={plan} activePlan={activePlan} onSelect={selectPlan} />;
        })}
        {activePlan === currentPlan ? (
          <div>
            <ButtonOverride isActive={true} onClick={handleCancellation}>
              Cancel Subscription
            </ButtonOverride>
          </div>
        ) : (
          <div>
            <ButtonOverride isActive={true} onClick={handlePlanChange}>
              Subscribe to the {upperFirst(activePlanName)} plan
            </ButtonOverride>
          </div>
        )}
      </PlansContainer>
    );
  }

  return render();
}

export default SelectedPlansOptionsSection;
